import { get } from 'svelte/store'
import { configStore, providerStore } from '@src/stores'
import { ethers } from '@credenza3/core-web-evm-ext'
import { getAddress } from '@src/passport/blockchain'
import { currentPpaActionsStore, fn, pageStore, ppaStore } from '@packages/stores'
import { Pages } from '@packages/utils/enums'

type TNftConfig = {
  address: string
  tokens: string[]
  type: 'ERC1155' | 'ERC721'
}

type TMembershipConfig = {
  address: string
  owner: string
}

type TSetFn = (val: { loading: boolean; active: boolean }) => void
type TUpdateFn = (val: { loading?: boolean; active?: boolean }) => void

const checkNft = async (nftConfigArr: TNftConfig[] = [], set: TSetFn) => {
  if (!nftConfigArr?.length) return false

  const provider = get(providerStore)
  const userAddress = await getAddress()

  for (const nftConfig of nftConfigArr) {
    if (nftConfig?.type === 'ERC721') {
      const { erc721MinAbi } = await import('../abi')
      const contract = new ethers.Contract(nftConfig.address, erc721MinAbi, provider)
      for (const tokenId of nftConfig.tokens) {
        const owner = await contract.ownerOf(tokenId)
        if (owner === userAddress) {
          set({ loading: false, active: false })
          return true
        }
      }
    } else {
      //1155 by default
      const { erc1155MinAbi } = await import('../abi')
      const contract = new ethers.Contract(nftConfig.address, erc1155MinAbi, provider)
      const [balance] = await contract.balanceOfBatch([userAddress], nftConfig.tokens)
      if (balance > 0) {
        set({ loading: false, active: false })
        return true
      }
    }
  }
  return false
}

const checkMembership = async (membershipConfigArr: TMembershipConfig[] = [], set: TSetFn) => {
  if (!membershipConfigArr?.length) return false

  const { checkMembership } = get(fn)

  for (const membershipConfig of membershipConfigArr) {
    const { isMember } = await checkMembership(membershipConfig.owner, membershipConfig.address)
    if (isMember) {
      set({ loading: false, active: false })
      return true
    }
  }
  return false
}

const checkPayPerArticleState = async (currentPath: string, set: TSetFn, update: TUpdateFn) => {
  const { payPerArticle } = get(configStore)?.content || {}
  for (const articleConfig of payPerArticle || []) {
    const uriMasks = articleConfig.uriMasks || []
    if (!uriMasks.length) continue

    for (const mask of uriMasks) {
      if (!currentPath.includes(mask)) continue

      currentPpaActionsStore.set({
        primaryAction: articleConfig.primaryAction,
        secondaryAction: articleConfig.secondaryAction,
      })
      set({ loading: true, active: true })
      if (await checkNft(articleConfig.nfts || [], set)) return
      if (await checkMembership(articleConfig.memberships || [], set)) return
    }
    update({ loading: false })
  }
}

let isPPAObserverCreated = false
export const createPayPeyArticleObserver = () => {
  if (isPPAObserverCreated) return
  isPPAObserverCreated = true

  const { update, set } = ppaStore
  const pathObservable = () => document.location.pathname + document.location.search
  const pageObservable = () => get(pageStore)
  let currentPathValue: string | null = null

  const observer = new MutationObserver(() => {
    const newPathValue = pathObservable()
    const newPageValue = pageObservable()
    if (currentPathValue === newPathValue && newPageValue !== Pages.PAYMENT_RESULT) return
    currentPathValue = newPathValue
    void checkPayPerArticleState(currentPathValue, set, (partialVal) =>
      update((prevVal) => ({ ...prevVal, ...partialVal })),
    )
  })

  providerStore.subscribe((provider) => {
    if (!provider) {
      observer.disconnect()
      return
    }
    observer.observe(document.body, { childList: true, subtree: true })
  })
}
